import is from '@sindresorhus/is';
import type z from 'zod';

import type { JobProfileComponent, jobProfileStructuredDescriptionSchema } from '~/shared/data-contract/job-profile';
import type { LanguageCodeForJSA } from '~/shared/internationalization';

import { addSkillsToCompetencies } from '../job-profile-section/skills/use-skills-with-compentencies';
import type { CompetencyWithSkills } from '../job-profile-section/types';

type StructuredDescription = z.infer<typeof jobProfileStructuredDescriptionSchema>;

const findMatchingSection = (
    sections: readonly StructuredDescription[] | undefined,
    id: string | undefined,
    tag: string,
): StructuredDescription | undefined => {
    if (is.undefined(sections)) return undefined;

    const idToMatch = id ?? tag;
    return sections.find((s) => (s.id ?? s.tag) === idToMatch) ?? sections.find((s) => s.tag === tag);
};

export const useCompetenciesWithSkillsAndDiff = (
    details: JobProfileComponent | undefined,
    compareWith: JobProfileComponent | undefined,
    type: 'added' | 'removed',
): readonly CompetencyWithSkills[] => {
    const competenciesWithSkills = addSkillsToCompetencies(
        [...(details?.competencies?.values() ?? [])],
        details?.skills ?? [],
    );

    return competenciesWithSkills.map((competency) => {
        return {
            ...competency,
            diff: compareWith?.competencies?.get(competency.id) !== undefined ? undefined : type,

            skills: competency.skills.map((skill) => {
                return {
                    ...skill,
                    diff: compareWith?.skills?.find((s) => s.id === skill.id) !== undefined ? undefined : type,
                };
            }),
        };
    });
};

const stripOuterPTag = (html: string): string => {
    return html.replace(/^<p>/, '').replace(/<\/p>$/, '');
};

export const matchSections = (
    old: JobProfileComponent,
    current: JobProfileComponent,
    activeLanguage: LanguageCodeForJSA,
) => {
    const currentStructured = current.translations.find((t) => t.language === activeLanguage)?.structured;
    const oldStructured = old.translations.find((t) => t.language === activeLanguage)?.structured;

    return (
        currentStructured?.map((section) => ({
            current: { ...section, value: stripOuterPTag(section.value) },
            old: findMatchingSection(oldStructured, section.id, section.tag),
        })) ?? []
    );
};
